import React, { useState } from 'react';
import {
  Compass,
  Building2,
  Network,
  DollarSign,
  TrendingUp,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { RoadmapPhase, RoadmapStepValidation, PhaseProgress } from '../../types/roadmap';
import { RoadmapStepItem } from './RoadmapStepItem';

interface RoadmapPhaseCardProps {
  phase: RoadmapPhase;
  validations: RoadmapStepValidation[];
  phaseProgress: PhaseProgress;
  onUpdateValidation: (
    phaseKey: string,
    stepKey: string,
    data: { is_manually_validated?: boolean; note?: string }
  ) => Promise<void>;
  onForceValidation: (phaseKey: string, stepKey: string, force: boolean) => Promise<void>;
}

const PHASE_ICONS = {
  Compass,
  Building2,
  Network,
  DollarSign,
  TrendingUp
};

const PHASE_COLORS = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    icon: 'text-blue-600',
    progress: 'bg-blue-600'
  },
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: 'text-green-600',
    progress: 'bg-green-600'
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    icon: 'text-purple-600',
    progress: 'bg-purple-600'
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: 'text-amber-600',
    progress: 'bg-amber-600'
  },
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    icon: 'text-emerald-600',
    progress: 'bg-emerald-600'
  }
};

export const RoadmapPhaseCard: React.FC<RoadmapPhaseCardProps> = ({
  phase,
  validations,
  phaseProgress,
  onUpdateValidation,
  onForceValidation
}) => {
  const [isExpanded, setIsExpanded] = useState(phaseProgress.progress_percentage > 0);
  const [editingStepKey, setEditingStepKey] = useState<string | null>(null);

  const Icon = PHASE_ICONS[phase.icon as keyof typeof PHASE_ICONS] || Compass;
  const colors = PHASE_COLORS[phase.color as keyof typeof PHASE_COLORS] || PHASE_COLORS.blue;

  return (
    <div className={`border rounded-xl overflow-hidden ${colors.border}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full px-6 py-4 flex items-center gap-4 ${colors.bg} hover:opacity-90 transition-opacity`}
      >
        <div className={`p-3 bg-white rounded-lg ${colors.icon}`}>
          <Icon className="w-6 h-6" />
        </div>

        <div className="flex-1 text-left">
          <h3 className={`text-lg font-semibold ${colors.text}`}>{phase.label}</h3>
          <p className="text-sm text-gray-600 mt-1">{phase.description}</p>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className={`text-sm font-medium ${colors.text}`}>
              {phaseProgress.completed_steps} / {phaseProgress.total_steps}
            </div>
            <div className="w-24 bg-white bg-opacity-50 rounded-full h-2 mt-1">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${colors.progress}`}
                style={{ width: `${phaseProgress.progress_percentage}%` }}
              />
            </div>
          </div>

          {isExpanded ? (
            <ChevronDown className={`w-5 h-5 ${colors.text}`} />
          ) : (
            <ChevronRight className={`w-5 h-5 ${colors.text}`} />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="p-6 bg-white space-y-4">
          {phase.steps.map((step) => {
            const validation = validations.find(
              (v) => v.phase_key === phase.key && v.step_key === step.key
            );

            return (
              <RoadmapStepItem
                key={step.key}
                step={step}
                validation={validation || null}
                onUpdateValidation={async (data) => {
                  await onUpdateValidation(phase.key, step.key, data);
                }}
                onForceValidation={async (force) => {
                  await onForceValidation(phase.key, step.key, force);
                }}
                isEditing={editingStepKey === step.key}
                onToggleEdit={() => {
                  setEditingStepKey(editingStepKey === step.key ? null : step.key);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
